import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { Grid, Text } from "@mantine/core";
import { useListState } from "@mantine/hooks";
import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { usePostApi } from "src/hooks";

import { parseChecklists } from "../CheckList.utils";
import { useChecklistsContext } from "../CheckListsContext";
import CheckListTableRow from "./CheckListTableRow";
import styles from "./CheckListTable.module.scss";
import { StyledGridCol } from "../../../../Summary/VerifyEntries/VerifyEntries.styled";

const headers = [
  { label: "", colSpan: 1 },
  { label: "Name", colSpan: 2 },
  { label: "Logbooks", colSpan: 3 },
  { label: "Description", colSpan: 3 },
  { label: "Version", colSpan: 1, textAlign: "center" as const },
  { label: "", colSpan: 2 },
];

const CheckListTable = () => {
  const { checklists, fetchChecklists } = useChecklistsContext();
  const { lbid } = useParams();
  const [state, handlers] = useListState(checklists);
  const { post } = usePostApi();
  const isReordering = useRef(false);

  useEffect(() => {
    if (checklists) handlers.setState(parseChecklists(checklists));
    // DO NOT ADD `handlers` to dep array as it causes performance issues!
  }, [checklists]);

  const postRequest = async (params: Record<string, any>) => {
    await post({
      request: "save-checklist",
      body: { params },
      onSuccess: () => {
        fetchChecklists({ params: { lbid, archived: 0 } }, true);
      },
    });
  };

  useEffect(() => {
    if (lbid) {
      if (lbid !== "*") fetchChecklists({ params: { lbid, archived: 0 } });
      else fetchChecklists({ params: { archived: 0 } });
    }
  }, [lbid]);

  const handleDragEnd = ({ destination, source }: any) => {
    if (!destination) return;

    isReordering.current = true;
    handlers.reorder({ from: source.index, to: destination.index });

    const updatedItem = state[source.index];
    void postRequest({
      ...updatedItem,
      desc: updatedItem.description,
      sort: destination.index + 1,
    });

    isReordering.current = false;
  };

  const items = state.map((item, index) => (
    <CheckListTableRow
      key={`table-row-c-${index}`}
      item={item}
      index={index}
      row={item}
    />
  ));

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className={styles.tableContainer}>
        <Grid className={styles.headerRow}>
          {headers.map(c => (
            <StyledGridCol
              key={`settings-c-${c.label}`}
              span={c.colSpan}
              textAlign={c.textAlign}
              justify={c.textAlign === "center" ? "center" : undefined}>
              <Text className={styles.headerLabel}>{c.label}</Text>
            </StyledGridCol>
          ))}
        </Grid>
        <div className={styles.tableBody}>
          <Droppable droppableId="dnd-list" direction="vertical">
            {provided => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className={styles.rowList}>
                {items}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </div>
    </DragDropContext>
  );
};

export default CheckListTable;
